/**
 * English — the source of truth for every customer-facing string on the
 * tracker. Other languages are `Partial` and fall back to this file key by key,
 * so a missing translation shows English rather than a blank or a raw key.
 *
 * Conventions
 *  - {name}      interpolated value
 *  - <b>…</b>    bold run (the only markup supported)
 *  - "\n"        separates list items in the `*.s` (steps) keys
 *  - err.<key>.t / .d / .s   title / description / steps of a known issue
 */
const en = {
  // ── Shared ────────────────────────────────────────────────────
  'lang.label': 'Language',
  'nav.tracker': 'Order Tracker',
  'nav.robux': 'Robux Order',
  'common.loading': 'Loading your order…',
  'common.order': 'Order #{id}',
  'common.submit': 'Submit',

  // ── Landing ───────────────────────────────────────────────────
  'landing.title': 'Track your order',
  'landing.sub': 'Enter your order ID to see live delivery status',
  'landing.header': 'Your Order ID',
  'landing.label': 'Order ID',
  'landing.placeholder': 'Enter your order ID',
  'landing.btn': 'Track Order',

  // ── Terms popup (coins order, before details are entered) ─────
  'terms.title': 'Before you continue',
  'terms.intro': 'Please read and accept the points below to continue with your order.',
  'terms.p1': '<b>You are responsible for the account you enter.</b> The moment the account enters our system the transfer is automatic, so please double-check it is the right account — we cannot redirect coins afterwards.',
  'terms.p2': '<b>The whole process is automatic.</b> Its speed, fast or slow, cannot be changed or controlled — our system is synced with an EA detection system, so it continues only while the process is judged safe and stops when it is not.',
  'terms.p3': '<b>We do not cover EA\'s {pct}% tax.</b> You ordered {gross}K coins, so you will receive <b>{net}K coins</b> net.',
  'terms.p4': '<b>If anything goes wrong with the transfer, talk to the staff member handling your order first.</b> Do not open a dispute straight away — it does not speed things up, it only makes it harder to solve.',
  'terms.agree': 'I have read and agree to all of the above',
  'terms.accept': 'I agree — continue',
  'terms.decline': 'I do not agree',
  'terms.declinedTitle': 'No problem',
  'terms.declinedBody': 'No problem — nothing has been started on your account. If any point is unclear, ask the staff member handling your order. You can come back to this page and continue whenever you are ready.',
  'terms.back': 'Go back',

  // ── EA tax (shown wherever the amount is shown) ───────────────
  'tax.hero': '{gross}K bought · {net}K reaches your club',
  'tax.noteTitle': 'EA takes {pct}% of every coin sale',
  'tax.noteBody': 'You bought <b>{gross}K coins</b>. EA\'s {pct}% transfer tax is not covered by us, so <b>{net}K coins</b> will land in your club — that is the amount your order covers.',
  'tax.finished': '<b>{net}K coins</b> have landed in your club ({gross}K bought, minus EA\'s {pct}% tax).',

  // ── Track (coins) — hero ──────────────────────────────────────
  'hero.loading': 'Loading…',
  'hero.notFound': 'Order Not Found',
  'hero.unknown': 'Unknown',
  'hero.cancelled': 'Order Cancelled',
  'hero.awaiting': 'Awaiting Your Details',
  'hero.delivered': 'Delivered Successfully!',
  'hero.dailyLimit': 'Daily Limit Reached',
  'hero.no2fa': 'Enable Login Verification',
  'hero.newBA': 'New Backup Code Needed',
  'hero.wrongPass': 'Incorrect Login Credentials',
  'hero.processing': 'Processing',
  'hero.paused': 'Order Paused',
  'hero.delivering': 'Delivering Your Coins',
  'badge.cancelled': 'CANCELLED',
  'badge.action': 'ACTION REQUIRED',
  'badge.complete': 'COMPLETE',
  'badge.autoResume': 'AUTO-RESUMING TOMORROW',
  'badge.support': 'CONTACT SUPPORT',
  'badge.retry': 'AUTO-RETRY',
  'badge.progress': 'IN PROGRESS',
  'hero.pct': '{pct}% delivered · {delivered}K of {ordered}K coins',
  'hero.refresh': 'Refresh',

  // ── Track (coins) — messages ──────────────────────────────────
  'msg.notFound': 'Order not found',
  'msg.netRetry': 'Network error — retrying…',
  'msg.submitFail': 'Failed to submit. Please try again.',
  'msg.net': 'Network error. Please try again.',
  'msg.resumed': 'Order resumed! Refreshing status…',
  'msg.waitLogout': 'Please wait a moment after logging out, then try again.',
  'msg.cantResumeWhy': 'Can\'t resume yet — {reason}. It will resume automatically.',
  'msg.cantResume': 'Can\'t resume just yet — your order will resume automatically. Try again shortly.',
  'msg.resumeFail': 'Resume failed. Please contact support.',
  'msg.retrying': 'Retrying now! Refreshing status…',
  'msg.retryFail': 'Retry failed — please try again shortly.',
  'msg.cancelledTitle': 'This order has been cancelled.',
  'msg.cancelledBody': 'Please contact the person who set up your order if you think this is a mistake.',

  // ── Track (coins) — pending form ──────────────────────────────
  'pending.banner': 'Your order for <b>{amount}K coins</b> on <b>{platform}</b> is ready. Enter your EA account details below to start the delivery. Your credentials are sent securely and never stored.',
  'pending.header': 'Your EA Account Details',
  'pending.logout': '<b>Before submitting:</b> Log out of the EA app, FUT web app, and companion app on all devices.',
  'pending.email': 'EA Account Email',
  'pending.emailPh': 'your@email.com',
  'pending.password': 'EA Account Password',
  'pending.passwordPh': 'Your EA password',
  'pending.ba1': 'Backup Code #1',
  'pending.ba1Hint': '— from EA Security Settings',
  'pending.ba1Ph': 'e.g. 12345678',
  'pending.ba2': 'Backup Code #2',
  'pending.ba3': 'Backup Code #3',
  'pending.optional': '— optional',
  'pending.hideExtra': '− Hide extra codes',
  'pending.showExtra': '+ Add more backup codes (recommended)',
  'pending.start': 'Start My Delivery',
  'pending.starting': 'Starting delivery…',
  'pending.locked': 'Please read and accept the terms to continue.',
  'pending.reopenTerms': 'Review the terms again',

  // ── Track (coins) — active ────────────────────────────────────
  'active.delivered': 'Your coins have been delivered! ⚽',
  'active.thanks': 'Thank you for choosing 911GamingStore. Enjoy!',
  'active.stayOut': '<b>Stay logged out</b> of the EA app, FUT web app, and companion app on all devices while your order is active.',
  'active.meta': ' · ~10 min per 100K · Daily limit: 1–3M coins (auto-resumes next day)',
  'active.dailyHeader': 'Daily Transfer Limit Reached',
  'active.dailyBody': 'The daily transfer limit has been reached for today. This is a normal safeguard — your order will automatically resume tomorrow. No action is needed on your end. You can log in, play normally, and even spend the coins if you want — the order will be resumed automatically in ~30 hours. You can check how long remaining till the daily limit resets using the button below. Thanks for your patience 👍',
  'active.retryBtn': 'I\'ve fixed it — Retry',
  'active.retrying': 'Retrying…',
  'metric.ordered': 'Ordered',
  'metric.willReceive': 'You receive',
  'metric.delivered': 'Delivered',
  'metric.remaining': 'Remaining',
  'progress.title': 'Delivery Progress',
  'progress.starting': 'Starting…',
  'progress.inProgress': 'In progress',
  'progress.complete': 'Complete',

  // ── Track (coins) — correction forms ──────────────────────────
  'ba.titleNo2fa': 'Enable Login Verification & Enter Backup Code',
  'ba.titleNew': 'New Backup Code Required',
  'ba.descNo2fa': 'Your EA account has no Login Verification set up. Enable it first, then generate backup codes and enter one below:',
  'ba.descNew': 'Your backup code is incorrect or expired. Follow these steps then enter a new code below:',
  'ba.no2faSteps': 'Go to <b>account.ea.com</b> → Security → Login Verification\nEnable verification — choose Authenticator App or Email\nAfter enabling, go to <b>Backup Codes</b> and generate a new set\nCopy one of the new codes and enter it below',
  'ba.newSteps': 'Go to <b>account.ea.com</b> → Security → Login Verification → Backup Codes\nGenerate a new set of backup codes\nCopy one of the new codes and enter it below',
  'ba.placeholder': 'New backup code',
  'ba.ok': 'Backup code updated! Retrying your order…',
  'ba.fail': 'Failed. Double-check the code or contact support.',
  'cred.title': 'Update Your Login Credentials',
  'cred.desc': 'The email or password appears to have changed. Please verify and update your credentials below:',
  'cred.steps': 'Go to <b>account.ea.com</b> and verify your email and password are correct\nIf you changed your password recently, enter the new one below\nMake sure you are logged out of all EA apps before submitting',
  'cred.emailPh': 'EA Account Email (if changed)',
  'cred.passPh': 'New EA Account Password',
  'cred.ok': 'Credentials updated! Retrying your order…',
  'cred.fail': 'Failed to update. Please contact support.',
  'resume.title': 'Order Paused — Resume When Ready',
  'resume.desc': 'Your order is paused. Make sure you are logged out of all EA apps, then click Resume.',
  'resume.btn': 'Resume Order',
  'resume.busy': 'Resuming…',
  'cool.title': 'Check Time Remaining',
  'cool.btn': 'Check Cooldown',
  'cool.busy': 'Checking…',
  'cool.desc': 'Check how long until the daily transfer limit resets and your order resumes automatically.',
  'cool.ready': 'Limit has reset! Your order will resume shortly.',
  'cool.hm': '~{h}h {m}m remaining',
  'cool.min': '~{m} min remaining',
  'shot.before': 'Account Proof (Before)',
  'shot.latest': 'Latest Transfer Proof',
  'shot.none': 'Not yet available',
  'row.status': 'Status',
  'row.accountCheck': 'Account Check',
  'row.economy': 'Economy State',
  'row.balance': 'Account Balance',
  'row.created': 'Order Created',
  'row.coins': '{n} coins',

  // ── Shop with us + footer ─────────────────────────────────────
  'shop.eyebrow': 'More than FC coins',
  'shop.title': 'Everything you play. One store.',
  'shop.lede': 'Game top-ups, gift cards and game keys at clear, low prices — delivered automatically, 24/7.',
  'shop.from': 'From {price}',
  'shop.floor': 'Prices start at {price}',
  'shop.browse': 'Browse everything',
  'shop.cta': 'Shop at 911 Gaming Store',
  'shop.pay': 'Pay with',
  'shop.f1t': 'Low, clear prices',
  'shop.f1d': 'The full price is shown before you pay — no surprises.',
  'shop.f2t': 'Delivered automatically',
  'shop.f2d': 'Most orders land on their own, day or night.',
  'shop.f3t': 'Account safety first',
  'shop.f3d': 'Your credentials are never stored.',
  'shop.f4t': 'Real support, 24/7',
  'shop.f4d': 'People, not bots, with you until the order is done.',
  'shop.cat.game-top-ups': 'Game top-ups',
  'shop.cat.gift-cards': 'Gift cards',
  'shop.cat.game-keys': 'Game keys',
  'footer.desc': 'Game top-ups, gift cards and game keys with clear prices, fast delivery and a real support team that stays with you until every order is done.',
  'footer.shop': 'Shop',
  'footer.help': 'Help',
  'footer.track': 'Track an order',
  'footer.contact': 'Contact support',

  // ── Known coin issues ─────────────────────────────────────────
  'err.wrongPersona.t': 'Wrong Persona Selected',
  'err.wrongPersona.d': 'The wrong FUT persona is selected on your account.',
  'err.wrongPersona.s': 'Open the FUT web app at www.ea.com/fifa/ultimate-team/web-app\nClick your profile icon and switch to the correct FUT persona\nLog out completely from all devices and browsers',
  'err.captcha.t': 'CAPTCHA Verification Required',
  'err.captcha.d': 'EA is requesting a security check on your account.',
  'err.captcha.s': 'Open the FUT web app at www.ea.com/fifa/ultimate-team/web-app\nLog in and complete the CAPTCHA verification if prompted\nLog out completely',
  'err.wrongUserPass.t': 'Incorrect Login Credentials',
  'err.wrongUserPass.d': 'The email or password you provided is wrong or has been changed since you submitted.',
  'err.wrongUserPass.s': 'Verify your EA account email and password are still correct\nReset your password at account.ea.com if needed\nLet the person who set up your order know so they can update your credentials',
  'err.wrongBA.t': 'Backup Code is Wrong',
  'err.wrongBA.d': 'Your backup code is incorrect or has already been used.',
  'err.wrongBA.s': 'Go to account.ea.com → Security → Login Verification → Backup Codes\nGenerate a new set of backup codes\nEnter one of the new codes in the form below',
  'err.No2FA.t': 'Login Verification (2FA) Not Set Up',
  'err.No2FA.d': 'Your EA account has no Login Verification enabled. This is required for the transfer to work.',
  'err.No2FA.s': 'Go to account.ea.com → Security → Login Verification\nEnable verification — choose Authenticator App or Email\nAfter enabling, go to Backup Codes and generate a new set\nEnter one of the new backup codes in the form below',
  'err.noTM.t': 'No Web App Transfer Market Access',
  'err.noTM.d': 'This EA account cannot use the Transfer Market on the FUT Web App. This is not the Transfer Market inside the game — they are separate, so it can work in the game and still be locked on the Web App.',
  'err.noTM.s': 'Open the FUT Web App at www.ea.com/fifa/ultimate-team/web-app and go to Transfers → Transfer Market\nIf it is locked or unavailable there, complete the FUT tutorial and keep playing matches until EA unlocks it for your account\nCheck that your EA Play / EA Pro subscription is active if required\nYour order stays open — once the Web App Transfer Market works, let the person who set up your order know and the transfer continues',
  'err.tlFull.t': 'Transfer List is Full',
  'err.tlFull.d': 'Your FUT transfer list is at maximum capacity and blocking new transfers.',
  'err.tlFull.s': 'Open the FUT web app or EA app on your phone\nGo to Transfers → Transfer List\nSell or remove some items to free up at least 5 slots\nLog out completely',
  'err.receiverTLFull.t': 'Your Transfer List is Full',
  'err.receiverTLFull.d': 'Your Transfer List has no space left — coins cannot be sent to your account until you free up some slots.',
  'err.receiverTLFull.s': 'Open the FUT web app at www.ea.com/fifa/ultimate-team/web-app\nGo to Transfers → Transfer List\nSell or Quick Sell items to free up at least 5 slots\nLog out completely from all EA apps and browsers',
  'err.FailedReceiverDeviceBan.t': 'Your Account Has Been Flagged by EA',
  'err.FailedReceiverDeviceBan.d': 'EA has placed a device-level block on your account. This requires intervention.',
  'err.FailedReceiverDeviceBan.s': 'Do not log into your EA account — let the person who set up your order know immediately',
  'err.FailWebAppNotYetUnlocked.t': 'FUT Web App Not Yet Unlocked',
  'err.FailWebAppNotYetUnlocked.d': 'Your EA account has not yet unlocked the FUT Web App, which is required for transfers.',
  'err.FailWebAppNotYetUnlocked.s': 'Open FIFA/EA FC on your console or PC\nComplete the FUT tutorial and play at least a few matches\nOnce you can access the FUT Web App, let the person who set up your order know',
  'err.FailWebAppCustomerLocked.t': 'Your FUT Web App Session is Locked',
  'err.FailWebAppCustomerLocked.d': 'Your EA account web session is currently locked — usually because you are logged in on another device.',
  'err.FailWebAppCustomerLocked.s': 'Log out of the FUT web app at www.ea.com/fifa/ultimate-team/web-app\nLog out of the EA app on your phone and all other devices\nWait 5 minutes, then try again',
  'err.calcErrorMaintenance.t': 'Temporary EA Maintenance',
  'err.calcErrorMaintenance.d': 'EA is currently under maintenance — retrying automatically. No action needed.',
  'err.noPlayer.t': 'Locating Suitable Transfer (Retrying)',
  'err.noPlayer.d': 'No suitable player card found at the moment — retrying automatically.',
  'err.safetyHold.t': 'Waiting for a safe transfer window',
  'err.safetyHold.d': 'Our system detects when EA\'s transfer-monitoring system is active and scanning coin movements across the market. Right now it is showing heightened activity, so we are holding your transfer to keep it safe. This affects the whole market, not your account. As soon as EA\'s system settles, your order resumes automatically. Nothing needed from you.',
  'err.belowMinTransfer.t': 'Transfer Below Minimum Amount',
  'err.belowMinTransfer.d': 'The remaining amount is below the minimum transfer threshold. You can resume to continue with a smaller transfer.',
  'err.belowMinTransfer.s': 'Click Resume below to continue delivery with the remaining amount',
  'err.notEnoughCoins.t': 'Not Enough Coins in Account',
  'err.notEnoughCoins.d': 'Your account needs a minimum of 10,000 coins to process transfers.',
  'err.notEnoughCoins.s': 'Buy any cheap player from the Transfer Market to get some coins in your account\nOnce you have at least 10,000 coins available, click Retry below',
  'err.unassignedItemsPresent.t': 'Unassigned Items Present',
  'err.unassignedItemsPresent.d': 'There are items sitting in your account that need to be assigned before transfers can continue.',
  'err.unassignedItemsPresent.s': 'Open the FUT web app or EA app\nGo to Club → Unassigned Items\nMove all items to your club or transfer list\nLog out completely',
  'err.console.t': 'Still Logged Into Ultimate Team on Console',
  'err.console.d': 'Your account is active in Ultimate Team on your PlayStation or Xbox.',
  'err.console.s': 'On your console, open FIFA/EA FC and go to Ultimate Team\nPress Options/Start → Quit to Main Menu (do not just close the game)\nWait for the game to fully exit Ultimate Team',
  'err.loginFailed.t': 'Login Failed — Log Out Everywhere',
  'err.loginFailed.d': 'EA rejected the login, likely because your account is active on another device or browser.',
  'err.loginFailed.s': 'Log out of the EA app on all your phones and tablets\nLog out of the FUT web app at www.ea.com/fifa/ultimate-team/web-app\nLog out of the FIFA/FC companion app if you use it',
  'err.LoginFailedDeviceBan.t': 'Login Blocked by EA',
  'err.LoginFailedDeviceBan.d': 'EA has blocked the login from our system. This requires our team to act.',
  'err.LoginFailedDeviceBan.s': 'Do not log into your EA account yourself — let the person who set up your order know right away',
  'err.FailedSessionExpiredCustomerLoggedIn.t': 'You Are Currently Logged Into EA App',
  'err.FailedSessionExpiredCustomerLoggedIn.d': 'Your account is active in the EA app or web app on another device.',
  'err.FailedSessionExpiredCustomerLoggedIn.s': 'Log out of the FUT web app at www.ea.com/fifa/ultimate-team/web-app\nLog out of the EA app on your phone\nLog out of the FIFA/FC companion app',
  'err.FailLoggedInConsoleTo.t': 'You Are Currently Logged Into EA App',
  'err.FailLoggedInConsoleTo.d': 'Your account is active on another device or browser.',
  'err.FailLoggedInConsoleTo.s': 'Log out of the FUT web app and EA app on all devices',
  'err.FailedProxyConnectionError.t': 'Temporary Technical Issue',
  'err.FailedProxyConnectionError.d': 'A brief technical issue on our end — retrying automatically. No action needed.',
  'err.FailedProxyPoolExhausted.t': 'Temporary Network Issue',
  'err.FailedProxyPoolExhausted.d': 'A brief network issue on our end — retrying automatically. No action needed.',
  'err.FailedCouldNotStartTo.t': 'Starting Transfer (Retrying)',
  'err.FailedCouldNotStartTo.d': 'Having a momentary issue starting the transfer — retrying automatically.',
  'err.noClub.t': 'No FUT Club Found',
  'err.noClub.d': 'Your EA account has no active FUT club — coins cannot be received without one.',
  'err.noClub.s': 'Open FIFA/EA FC and start Ultimate Team\nComplete the initial FUT setup and play at least one game\nOnce your FUT club is set up, let the person who set up your order know',
  'err.wrongConsole.t': 'Platform Mismatch',
  'err.wrongConsole.d': 'The platform on your order does not match your EA account.',
  'err.wrongConsole.s': 'Let the person who set up your order know — they need to correct the platform',
  'err.daily.t': 'Daily Transfer Limit Reached',
  'err.senderTempban.t': 'Supplier Temporarily Paused',
  'err.senderTempban.d': 'Our supplier account has a brief EA cooldown. This is handled automatically — no action needed.',
  'err.transfersInProgress.t': 'Transfer In Progress',
  'err.transfersInProgress.d': 'Transfers are actively being processed — updating shortly.',

  // ── Order status values ───────────────────────────────────────
  'status.pending': 'Pending',
  'status.ready': 'Ready',
  'status.entered': 'Entered',
  'status.waitingForAssignment': 'Waiting for assignment',
  'status.active': 'Active',
  'status.finished': 'Finished',
  'status.interrupted': 'Interrupted',
  'status.tempbanCooldown': 'Cooldown',
  'status.cancelled': 'Cancelled',
  'status.completed': 'Completed',

  // ── Robux tracker ─────────────────────────────────────────────
  'rbx.heroLine': '{gross} R$ game pass · {net} R$ reaches you',
  'rbx.hero.pass': 'Send Your Game Pass',
  'rbx.hero.done': 'Robux Delivered!',
  'rbx.hero.delivering': 'Delivering Your Robux',
  'rbx.hero.retrying': 'Retrying',
  'rbx.notFoundTitle': 'We couldn\'t find that order',
  'rbx.createTitle': 'Create a game pass for {gross} R$',
  'rbx.taxIntro': 'Roblox keeps {pct}% of every game pass sale, so a pass priced at <b>{gross} R$</b> pays out <b>{net} R$</b> — that is the amount that will land in your account, and it is what your order covers.',
  'rbx.steps': 'Open Roblox → <b>Create</b> → pick any game you own\nGo to <b>Associated Items → Passes</b> and create a pass\nOpen the pass → <b>Configure → Sales</b>\nTurn <b>Item for Sale</b> on and set the price to exactly <b>{gross} R$</b>\nSend it below — the pass link, just its ID, or your Roblox username',
  'rbx.reqTitle': 'Your game pass must also meet these rules',
  'rbx.req': 'Turn <b>Managed / Regional Pricing OFF</b>\nIn the content rating questionnaire, answer <b>No</b> to every question\nThe game must be <b>Public</b> (if it is hidden: Archive → Restore → Make Public)\nYour Roblox account must be <b>age-verified (16+)</b>',
  'rbx.videoBtn': 'Not sure how to create a game pass? Watch the video guide',
  'rbx.tabPass': 'Pass link or ID',
  'rbx.tabUser': 'Roblox username',
  'rbx.passLabel': 'Game pass link or ID',
  'rbx.passPh': 'https://www.roblox.com/game-pass/000000000/…  or just 000000000',
  'rbx.reading': 'Reading game pass <b>#{id}</b> ✓',
  'rbx.badPaste': 'That doesn\'t look like a game pass link or ID yet — paste the full link, or just the numbers from it.',
  'rbx.userLabel': 'Roblox username',
  'rbx.userPh': 'The account that will receive the Robux',
  'rbx.userHint': 'We\'ll find the pass on your account ourselves. It still has to be priced at exactly <b>{gross} R$</b>, on sale, and in one of your public games.',
  'rbx.placeLabel': 'Game / place ID',
  'rbx.placePh': 'Only if you have more than one game',
  'rbx.sendPass': 'Send my game pass',
  'rbx.sendUser': 'Send my username',
  'rbx.sending': 'Sending…',
  'rbx.allSet': 'You\'re all set — nothing else to do',
  'rbx.sentDone': '<b>{net} R$</b> has been sent to your Roblox account{user}.',
  'rbx.sentWait': 'We have your game pass and your order is complete on our side. <b>{net} R$</b> is on its way to your Roblox account.',
  'rbx.holdTitle': 'Why it takes 5–7 days to spend',
  'rbx.holdBody1': 'Roblox holds the earnings from <em>every</em> game pass sale for a pending period before they can be used. Your Robux will show up as <b>pending</b> in your account first, then become spendable within <b>5–7 days</b>.',
  'rbx.holdBody2': 'This is how Roblox works for all game pass purchases — it is not something we or anyone else can speed up. Your order is done.',
  'rbx.favourTitle': 'One small favour',
  'rbx.favourBody': 'If everything looks right, please mark the order as <b>received</b> on Eldorado and leave a review. We\'re a small team and it genuinely makes a difference for us. 🙏',
  'rbx.eldoradoBtn': 'Confirm & review on Eldorado',
  'rbx.passOnFile': 'Game pass on file:',
  'rbx.passLabelShort': 'Game pass:',
  'rbx.providerDown': 'Live status is briefly unavailable — your order is unaffected.',
  'rbx.tryAgain': 'Try again',
  'rbx.retrying': 'Retrying…',
  'rbx.cancelledTitle': 'This order was cancelled',
  'rbx.cancelledBody': 'Contact the person who set up your order if you think this is a mistake.',
  'rbx.msg.noAccept': 'We could not accept that link',
  'rbx.msg.net': 'Could not reach the server. Check your connection.',
  'rbx.msg.retryWait': 'Could not retry just yet — please try again in a minute.',
  'rbx.msg.retryOk': 'Retrying now — this page updates on its own.',

  'rbxi.gamepass_not_found.t': 'We can\'t see your game pass yet',
  'rbxi.gamepass_not_found.d': 'Roblox has not published the pass, or it was deleted or renamed after you sent the link. We keep checking on our own, so a pass that was just created will usually be picked up within a few minutes.',
  'rbxi.gamepass_not_found.s': 'Open your game pass page and confirm it still exists\nMake sure the price is exactly the amount shown above\nCheck that the pass is On Sale\nThen press Try Again below',
  'rbxi.insufficient_customer_balance.t': 'Your order is queued',
  'rbxi.insufficient_customer_balance.d': 'We are topping up and your order will go through automatically. Nothing is needed from you.',
  'rbxi.unknown_error.t': 'Your order needs another attempt',
  'rbxi.unknown_error.d': 'Something interrupted the delivery. We retry these automatically — you can also trigger a retry yourself.',
  'rbxi.GAMEPASS_NOT_FOUND.t': 'That game pass couldn\'t be found',
  'rbxi.GAMEPASS_NOT_FOUND.d': 'Roblox has no pass at that link. Newly created passes can take a minute to appear.',
  'rbxi.GAMEPASS_NOT_FOUND.s': 'Open the link yourself and check that the pass loads\nCopy the link from your browser address bar while the pass is open\nWait a minute if you just created it, then submit again',
  'rbxi.GAMEPASS_PRICE_MISMATCH.t': 'The pass price does not match your order',
  'rbxi.GAMEPASS_PRICE_MISMATCH.d': 'The game pass must be priced at exactly the amount shown above — not the amount you will receive.',
  'rbxi.GAMEPASS_PRICE_MISMATCH.s': 'Open your game pass → Configure → Sales\nSet the price to exactly the amount shown above\nSave, then submit the link again',
  'rbxi.REGIONAL_PRICING_NOT_SUPPORTED.t': 'Regional pricing is on for this pass',
  'rbxi.REGIONAL_PRICING_NOT_SUPPORTED.d': 'Roblox shows a different price in different countries, so the pass cannot be bought at a fixed amount.',
  'rbxi.REGIONAL_PRICING_NOT_SUPPORTED.s': 'Open your game pass → Configure → Sales\nTurn Regional Pricing off\nSave, then submit the link again',
  'rbxi.GAMEPASS_SELLER_MISMATCH.t': 'That pass belongs to another account',
  'rbxi.GAMEPASS_SELLER_MISMATCH.d': 'The game pass must be created on the same Roblox account that should receive the Robux.',
  'rbxi.GAMEPASS_SELLER_MISMATCH.s': 'Create the pass on the account that will receive the Robux\nSubmit the new link',
  'rbxi.UNIVERSE_NOT_FOUND.t': 'We could not read the game behind that pass',
  'rbxi.UNIVERSE_NOT_FOUND.d': 'The pass has to belong to a game that is public.',
  'rbxi.UNIVERSE_NOT_FOUND.s': 'Open your game → Configure → make sure it is Public\nThen submit the link again',
  'rbxi.ROBLOX_USER_NOT_FOUND.t': 'We could not find that Roblox account',
  'rbxi.ROBLOX_USER_NOT_FOUND.d': 'Check the spelling of the username, or send your game pass link instead.',
  'rbxi.ROBLOX_USER_NOT_FOUND.s': 'Send the game pass link — it identifies your account by itself',
  'rbxi.OUT_OF_STOCK.t': 'Your order is in the queue',
  'rbxi.OUT_OF_STOCK.d': 'Robux are being restocked and your order is reserved. It completes automatically the moment stock is available — you do not need to do anything.',
  'rbxi.ROBLOX_DAILY_LIMIT.t': 'Roblox daily limit reached',
  'rbxi.ROBLOX_DAILY_LIMIT.d': 'Roblox caps how much can be sent to one account per day. Your order resumes automatically once the limit resets.',
  'rbxi.INSUFFICIENT_FUNDS.t': 'Your order is queued',
  'rbxi.INSUFFICIENT_FUNDS.d': 'Your order is reserved and will be completed shortly. Nothing is needed from you.',
  'rbxi.ORDER_ALREADY_EXISTS.t': 'This order is already running',
  'rbxi.ORDER_ALREADY_EXISTS.d': 'We already have your game pass — no need to send it again.',
};

export type MsgKey = keyof typeof en;
export type Messages = Record<MsgKey, string>;
export default en as Messages;
